import { useState, useCallback, useEffect, useRef } from 'react';
import { APIProvider, Map, Marker, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';

interface LocationPickerProps {
  onLocationSelect: (latitude: number, longitude: number, address?: string) => void;
  initialCenter?: { lat: number; lng: number };
  height?: string;
  showSearch?: boolean;
  /**
   * Posición controlada desde afuera (por ejemplo, cuando el usuario escribe
   * latitud/longitud a mano en un input). Si cambia, el mapa recentra y el
   * marcador se mueve, igual que con un clic o una búsqueda.
   */
  position?: { lat: number; lng: number } | null;
  /**
   * Si es `true`, el mapa ignora `height` y en cambio ocupa TODO el alto del
   * contenedor donde se lo monte (vía flexbox, `flex-1`) — para "homologar"
   * su alto con el de una columna vecina (ej. un formulario) en vez de un
   * valor fijo en px que puede quedar más alto o más bajo que esa columna.
   * El contenedor padre debe darle una altura real (ej. con `items-stretch`
   * en un `flex` fila, o una altura explícita).
   */
  fillParent?: boolean;
}

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

// Default center: PANAMÁ City
const DEFAULT_CENTER = { lat: 9.0765, lng: -79.5309 };
const DEFAULT_ZOOM = 11;

/**
 * Buscador de direcciones — migrado de `google.maps.places.Autocomplete`
 * (legacy) a `google.maps.places.PlaceAutocompleteElement` (Places API New).
 *
 * Motivo: Google dejó de habilitar la API legacy para proyectos de GCP
 * nuevos (creados después del 1° de marzo de 2025) — con una key de un
 * proyecto nuevo, `Autocomplete` tira "not available to new customers" y la
 * consola de Google directamente no deja activar "Places API" (la clásica).
 * Ver docs/PLACES_API_PENDIENTE.md (Opción B).
 *
 * `PlaceAutocompleteElement` es un WEB COMPONENT (`<gmp-place-autocomplete>`)
 * con su propio input interno (shadow DOM) — a diferencia del legacy, no se
 * puede "enganchar" a un `<input>` de Tailwind ya existente. Por eso este
 * componente ya no dibuja su propio input+ícono: monta el elemento de
 * Google dentro de `containerRef` y listo. El look del buscador ahora lo
 * decide Google (styling nativo del elemento), no Tailwind.
 *
 * El nombre del evento y la forma exacta del payload NO están confirmados
 * con una prueba en vivo end-to-end: el proyecto de la key de desarrollo
 * (`.env.development`) tiene "Places API (New)" SIN HABILITAR todavía
 * (Google devuelve 403 "Places API (New) has not been used in project ...
 * or it is disabled" al intentar buscar) — hay que habilitarla en
 * https://console.cloud.google.com/apis/library/places.googleapis.com para
 * el proyecto de esa key antes de poder probar la selección real.
 *
 * Lo que SÍ se confirmó en este repo:
 *   - `new placesLib.PlaceAutocompleteElement({ componentRestrictions })`
 *     construye y monta bien (`tagName === 'GMP-PLACE-AUTOCOMPLETE'`),
 *     verificado corriendo la librería real contra `maps.googleapis.com`.
 *   - Los tipos oficiales instalados (`@types/google.maps@3.58.1`) declaran
 *     el evento como clase `PlaceAutocompletePlaceSelectEvent` con la
 *     propiedad `place: google.maps.places.Place` DIRECTA en el evento (no
 *     `event.detail.place`, no `.toPlace()`).
 *   - La librería cargada en vivo (canal beta) además expone clases nuevas
 *     (`PlaceSelectEvent`, `PlacePredictionSelectEvent`) que no están en esa
 *     versión de los tipos — Google sigue moviendo esto (feature en beta).
 *
 * Por eso `handleSelect` lee `event.place` (lo documentado en los tipos
 * instalados) con fallback a `event.placePrediction?.toPlace()` (la forma
 * que documentan varias guías de Google para el flujo equivalente) — cubre
 * ambas variantes sin adivinar cuál "gana". Cuando puedas habilitar la API
 * y probar una búsqueda real, revisá en consola cuál de las dos rutas se
 * usa y se puede simplificar esto a una sola.
 */
function PlacesSearch({
  onPlaceSelect
}: {
  onPlaceSelect: (lat: number, lng: number, address: string) => void
}) {
  const map = useMap();
  const placesLib = useMapsLibrary('places');
  const containerRef = useRef<HTMLDivElement>(null);
  const elementRef = useRef<google.maps.places.PlaceAutocompleteElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Evita que el efecto de abajo dependa de `onPlaceSelect` (una prop nueva
  // en cada render de MapWithSearch reinicializaría el widget sin necesidad).
  const onPlaceSelectRef = useRef(onPlaceSelect);
  onPlaceSelectRef.current = onPlaceSelect;

  useEffect(() => {
    if (!placesLib || !containerRef.current || elementRef.current) return;

    let element: google.maps.places.PlaceAutocompleteElement;
    try {
      // `PlacesLibrary` (el tipo de `useMapsLibrary('places')`) todavía no
      // declara `PlaceAutocompleteElement` en @types/google.maps@3.58.1
      // aunque la clase sí existe y está documentada (confirmado además en
      // runtime, ver comentario de cabecera) — es un feature en canal beta,
      // los tipos van atrás. Cast puntual en vez de `as any` en toda la lib.
      const PlaceAutocompleteElementCtor = (
        placesLib as unknown as {
          PlaceAutocompleteElement: new (
            options: google.maps.places.PlaceAutocompleteElementOptions
          ) => google.maps.places.PlaceAutocompleteElement;
        }
      ).PlaceAutocompleteElement;
      element = new PlaceAutocompleteElementCtor({
        // Mismo sesgo que el legacy: predicciones dentro de Panamá.
        componentRestrictions: { country: 'pa' },
      });

      // El shadow DOM interno de Google declara `:host { color-scheme: light
      // dark; background-color: light-dark(#fff, #131314); ... }` — sigue el
      // modo oscuro del SISTEMA OPERATIVO, no el de esta app (que es manual,
      // vía clase `.dark`, ver `darkMode: 'class'` en tailwind.config.js).
      // Con el SO en modo oscuro esto salía con fondo negro aunque el resto
      // de la pantalla se viera claro. `color-scheme` en un div contenedor
      // NO alcanza (confirmado): la regla `:host` de adentro gana por ser
      // una declaración propia del elemento, no heredada. Hay que pisarla
      // en el HOST mismo — un estilo puesto directo en el elemento desde
      // afuera sí le gana a `:host` por diseño del spec de Shadow DOM.
      element.style.colorScheme = 'light';
    } catch (err) {
      console.error('❌ Error creando PlaceAutocompleteElement:', err);
      setError('Error al inicializar búsqueda. Verifica que "Places API (New)" esté habilitada para esta key.');
      return;
    }

    containerRef.current.appendChild(element);
    elementRef.current = element;
    setError(null);

    const handleSelect = async (event: any) => {
      try {
        // Ver comentario de cabecera: dos formas posibles del payload según
        // versión/canal de la librería.
        const rawPlace = event.place ?? (event.placePrediction ? event.placePrediction.toPlace() : null);
        if (!rawPlace) {
          console.warn('⚠️ Evento de selección sin place/placePrediction reconocible:', event);
          return;
        }

        const { place } = await rawPlace.fetchFields({
          fields: ['location', 'formattedAddress', 'displayName'],
        });

        if (!place.location) {
          console.warn('⚠️ Place sin location tras fetchFields');
          return;
        }

        const lat = place.location.lat();
        const lng = place.location.lng();
        const address = place.formattedAddress || place.displayName || '';

        if (map) {
          map.panTo({ lat, lng });
          map.setZoom(17);
        }

        onPlaceSelectRef.current(lat, lng, address);
      } catch (err) {
        console.error('❌ Error procesando el lugar seleccionado:', err);
      }
    };

    element.addEventListener('gmp-select', handleSelect);

    return () => {
      element.removeEventListener('gmp-select', handleSelect);
      element.remove();
      elementRef.current = null;
    };
  }, [placesLib, map]);

  return (
    <div>
      {/*
        Google monta acá su propio <gmp-place-autocomplete> (input + ícono
        + dropdown incluidos) — no hay input propio que estilizar. El fondo
        siempre claro se fuerza en el elemento mismo (`element.style.colorScheme`
        más arriba), no en este div — ver el comentario de ese punto.
      */}
      <div ref={containerRef} />
      {error && (
        <p className="mt-1 text-xs text-red-500">{error}</p>
      )}
    </div>
  );
}

// Inner map component that can access map context
function MapWithSearch({
  initialCenter,
  height,
  showSearch,
  onLocationSelect,
  markerPosition,
  setMarkerPosition,
  externalPosition,
  fillParent,
}: {
  initialCenter: { lat: number; lng: number };
  height: string;
  showSearch: boolean;
  onLocationSelect: (latitude: number, longitude: number, address?: string) => void;
  markerPosition: { lat: number; lng: number } | null;
  setMarkerPosition: (pos: { lat: number; lng: number } | null) => void;
  externalPosition?: { lat: number; lng: number } | null;
  fillParent?: boolean;
}) {
  const [selectedAddress, setSelectedAddress] = useState<string>('');
  const map = useMap();

  // Sincroniza el mapa cuando la posición cambia desde afuera (ej. el usuario
  // escribió latitud/longitud a mano) y no por un clic o búsqueda locales.
  useEffect(() => {
    if (!externalPosition) return;
    const sinCambios =
      markerPosition &&
      markerPosition.lat === externalPosition.lat &&
      markerPosition.lng === externalPosition.lng;
    if (sinCambios) return;

    setMarkerPosition(externalPosition);
    if (map) {
      map.panTo(externalPosition);
      map.setZoom(17);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalPosition, map]);

  const handleMapClick = useCallback(
    (event: any) => {
      let lat, lng;

      if (event?.detail?.latLng) {
        lat = event.detail.latLng.lat;
        lng = event.detail.latLng.lng;
      } else if (event?.latLng) {
        lat = typeof event.latLng.lat === 'function' ? event.latLng.lat() : event.latLng.lat;
        lng = typeof event.latLng.lng === 'function' ? event.latLng.lng() : event.latLng.lng;
      }

      if (lat && lng) {
        setMarkerPosition({ lat, lng });
        setSelectedAddress('');
        onLocationSelect(lat, lng);
      }
    },
    [onLocationSelect, setMarkerPosition]
  );

  const handlePlaceSelect = useCallback(
    (lat: number, lng: number, address: string) => {
      setMarkerPosition({ lat, lng });
      setSelectedAddress(address);
      onLocationSelect(lat, lng, address);
    },
    [onLocationSelect, setMarkerPosition]
  );

  return (
    <div className={fillParent ? 'flex h-full flex-col space-y-3' : 'space-y-3'}>
      {showSearch && (
        <PlacesSearch onPlaceSelect={handlePlaceSelect} />
      )}

      {selectedAddress && (
        <div className="text-sm text-secondary-600 dark:text-secondary-400 bg-secondary-100 dark:bg-secondary-700 px-3 py-2 rounded-lg">
          <span className="font-medium">Dirección:</span> {selectedAddress}
        </div>
      )}

      <div className={fillParent ? 'min-h-0 flex-1' : undefined} style={fillParent ? undefined : { height }}>
        <Map
          // `defaultCenter` es no-controlado (solo se lee al montar): si ya
          // hay una posición (ej. al volver a este paso del wizard con
          // coordenadas ya cargadas), el mapa abre centrado ahí en vez de
          // Ciudad de Panamá.
          defaultCenter={markerPosition ?? initialCenter}
          defaultZoom={DEFAULT_ZOOM}
          // "greedy" le robaba el scroll de la rueda del mouse a la página
          // entera (zoomeaba el mapa en vez de dejar scrollear el modal
          // cuando el cursor pasaba por encima) — "cooperative" es el modo
          // estándar de Google Maps: el scroll normal pasa de largo y el
          // zoom pide Ctrl+rueda (o dos dedos en trackpad/touch).
          gestureHandling="cooperative"
          disableDefaultUI={false}
          onClick={handleMapClick}
          style={{ width: '100%', height: '100%' }}
        >
          {markerPosition && (
            <Marker
              position={markerPosition}
              title="Ubicación seleccionada"
            />
          )}
        </Map>
      </div>
    </div>
  );
}

export function LocationPicker({
  onLocationSelect,
  initialCenter = DEFAULT_CENTER,
  height = '400px',
  showSearch = false, // Disabled by default until Places API is enabled
  position = null,
  fillParent = false,
}: LocationPickerProps) {
  const [markerPosition, setMarkerPosition] = useState<{ lat: number; lng: number } | null>(
    position
  );
  const [mapError, setMapError] = useState<string | null>(null);

  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY || GOOGLE_MAPS_API_KEY.length < 30) {
      console.warn('Google Maps API Key not configured properly');
    }
  }, []);

  // Si no hay API key, mostrar instrucciones (validación más permisiva)
  if (!GOOGLE_MAPS_API_KEY || GOOGLE_MAPS_API_KEY.length < 30) {
    return (
      <div
        className={`w-full bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 ${fillParent ? 'h-full' : ''}`}
        style={fillParent ? undefined : { height }}
      >
        <div className="text-center space-y-4">
          <h3 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200">
            Configuración de Google Maps requerida
          </h3>
          <p className="text-sm text-yellow-700 dark:text-yellow-300">
            Para usar el mapa interactivo, necesitas configurar una clave de API de Google Maps:
          </p>
          <ol className="text-left text-sm text-yellow-700 dark:text-yellow-300 space-y-2 max-w-md mx-auto">
            <li>1. Ve a <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer" className="underline font-semibold">Google Cloud Console</a></li>
            <li>2. Crea un proyecto o selecciona uno existente</li>
            <li>3. Habilita "Maps JavaScript API"</li>
            <li>4. Crea una clave de API en "Credenciales"</li>
            <li>5. Agrega la clave al archivo .env como VITE_GOOGLE_MAPS_API_KEY</li>
            <li>6. Reinicia el servidor de desarrollo</li>
          </ol>
          <div className="mt-4 p-3 bg-yellow-100 dark:bg-yellow-900/40 rounded text-xs text-yellow-800 dark:text-yellow-200 font-mono">
            Clave actual: {GOOGLE_MAPS_API_KEY || 'No configurada'}
            <br />
            Longitud: {GOOGLE_MAPS_API_KEY?.length || 0} caracteres (esperado: ~39)
            <br />
            <br />
            <strong>Importante:</strong> Después de actualizar .env, debes reiniciar el servidor:
            <br />
            Detén con Ctrl+C y ejecuta: npm run dev
          </div>
        </div>
      </div>
    );
  }

  if (mapError) {
    return (
      <div
        className={`w-full bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 ${fillParent ? 'h-full' : ''}`}
        style={fillParent ? undefined : { height }}
      >
        <div className="text-center space-y-4">
          <h3 className="text-lg font-semibold text-red-800 dark:text-red-200">
            Error al cargar el mapa
          </h3>
          <p className="text-sm text-red-700 dark:text-red-300">
            {mapError}
          </p>
          <button
            onClick={() => setMapError(null)}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={fillParent ? 'w-full h-full flex flex-col' : 'w-full'}>
      <APIProvider
        apiKey={GOOGLE_MAPS_API_KEY}
        // `PlaceAutocompleteElement` (ver PlacesSearch más arriba) solo está
        // disponible en el canal "beta" de la Maps JS API — confirmado
        // corriendo la librería real (con "v=beta" en el script funciona; el
        // canal por defecto de @vis.gl/react-google-maps no lo expone). Solo
        // se pide beta cuando el buscador está activo — el resto de los usos
        // de LocationPicker (sin showSearch) sigue en el canal estable.
        version={showSearch ? 'beta' : undefined}
        onLoad={() => console.log('Maps API loaded successfully')}
        onError={(error) => {
          console.error('Maps API error:', error);
          setMapError(`Error al cargar Google Maps. Verifica que la clave de API tenga Maps JavaScript API habilitada.`);
        }}
      >
        <MapWithSearch
          initialCenter={initialCenter}
          height={height}
          showSearch={showSearch}
          onLocationSelect={onLocationSelect}
          markerPosition={markerPosition}
          setMarkerPosition={setMarkerPosition}
          externalPosition={position}
          fillParent={fillParent}
        />
      </APIProvider>
    </div>
  );
}
